import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLiff } from "../../../liff/index.js";
import { fetchSiteLive, type SiteLiveData } from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, MetricGrid, ProgressBar, SeverityPill, EmptyMobile, MobileSpinner, PrimaryButton,
} from "../../../liff/components/Mobile.js";
import { buildDashboardUrl } from "@nexus/shared/links";

export function SitePlaceholderPage(): React.ReactElement {
  const { siteId } = useParams<{ siteId: string }>();
  const { session } = useLiff();
  const [data, setData] = useState<SiteLiveData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session || !siteId) return;
    fetchSiteLive(siteId, session.session_token)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session, siteId]);

  if (error) return (
    <MobileShell title="無法載入案場狀態"><Card><p className="text-sm text-danger">{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner />;

  const pct = Math.round(data.metrics.completion_rate * 100);
  return (
    <MobileShell title={data.site_name} subtitle={`${data.date_local} · 即時案場`}>
      <Card>
        <CardHeader title="今日完成率" hint={`${data.metrics.patrols_completed}/${data.metrics.patrols_expected}`} />
        <p className="text-3xl font-semibold mb-3">{pct}<span className="text-base text-text-secondary">%</span></p>
        <ProgressBar value={data.metrics.completion_rate} />
      </Card>

      <MetricGrid items={[
        { label: "完成 / 應巡", value: `${data.metrics.patrols_completed} / ${data.metrics.patrols_expected}`, tone: "default" },
        { label: "漏巡", value: String(data.metrics.overdue_count), tone: data.metrics.overdue_count > 0 ? "danger" : "success" },
        { label: "事件", value: String(data.metrics.incidents_count), tone: data.metrics.incidents_count > 0 ? "warning" : "success" },
        { label: "在勤", value: String(data.on_shift_officers.length), tone: "default" },
      ]} />

      <Card>
        <CardHeader title="即將到期窗口" hint={`${data.upcoming_windows.length} 筆`} />
        {data.upcoming_windows.length === 0 ? (
          <p className="text-xs text-text-muted">沒有未到期的點位窗口。</p>
        ) : (
          <ul className="divide-y divide-border-subtle -my-2">
            {data.upcoming_windows.map((w) => (
              <li key={w.id} className="py-2 flex items-center justify-between gap-2">
                <span className="text-sm truncate">{w.location_label}</span>
                <span className="text-xs text-text-muted shrink-0">{w.due_in_minutes} 分後</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="最新巡邏事件" hint={`${data.recent_events.length} 筆`} />
        <ul className="divide-y divide-border-subtle -my-2">
          {data.recent_events.map((e) => (
            <li key={e.id} className="py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm truncate">{e.location_label}</p>
                <p className="text-[11px] text-text-muted">{e.officer_name} · {e.occurred_at_local}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-wider shrink-0 ${e.verified ? "text-success" : "text-warning"}`}>
                {e.verified ? "已驗證" : "待驗證"}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <CardHeader title="未結案事件" hint={`${data.open_incidents.length} 筆`} />
        {data.open_incidents.length === 0 ? <EmptyMobile title="沒有未結案事件 🎉" /> : (
          <ul className="space-y-2">
            {data.open_incidents.map((i) => (
              <li key={i.id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium truncate">{i.title}</p>
                  <SeverityPill severity={i.severity} />
                </div>
                <p className="text-[11px] text-text-muted mt-1">{i.occurred_at_local} · {i.status === "open" ? "待處理" : i.status === "ack" ? "已確認" : "已解決"}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <PrimaryButton href={buildDashboardUrl(`/dashboard/monitor?site=${siteId}`)}>
        在 Dashboard 開啟此案場
      </PrimaryButton>
    </MobileShell>
  );
}
